import React, { useState, useMemo } from 'react';
import { Check, ChevronLeft, ChevronRight, Info, Star, X } from 'lucide-react';
import { useEffect } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getSettingForWeb, AddOrder } from '../../redux/actions/package';
import { useUser } from 'contexts';
import { toast } from 'react-toastify';
import { config } from 'config/config';
import { userProfile } from '../../redux/actions/user';

const PlanCarousel = ({ PackageData, currentPlan, onUpgrade, onClose, isExpired = false }) => {
   const dispatch = useDispatch();
   const [currentSlide, setCurrentSlide] = useState(0);
   const [selectedPlan, setSelectedPlan] = useState(null);
   const [hoveredPlan, setHoveredPlan] = useState(null);
   const [isTransitioning, setIsTransitioning] = useState(false);
   const [showSummaryModal, setShowSummaryModal] = useState(false);
   const [selectedPlanData, setSelectedPlanData] = useState(null);
   const [isProcessingPayment, setIsProcessingPayment] = useState(false);

   const WebSetting = useSelector((state) => {
      const webSetting = state.Package?.webSetting?.[0] || {};
      return {
         gstPer: webSetting.gstPer || 18,
         compName: webSetting.compName || 'Your Company Name',
         gstNo: webSetting.gstNo || 'GSTIN Not Available',
         address: webSetting.address || 'Address Not Available',
         ...webSetting,
      };
   });

   const { user } = useUser();

   useEffect(() => {
      dispatch(getSettingForWeb());
   }, [dispatch]);

   // Dark mode hook
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   // Filter out demo packages and format the data
   const filteredPackageData = useMemo(() => {
      const uniquePackages = [];
      const seenNames = new Set();

      PackageData.filter((pkg) => !pkg.isDemo && pkg.status === true).forEach((pkg) => {
         if (!seenNames.has(pkg.name)) {
            seenNames.add(pkg.name);
            uniquePackages.push({
               ...pkg,
               features: generateFeatures(pkg),
               popular: isPopularPlan(pkg.packageName),
            });
         }
      });

      return uniquePackages;
   }, [PackageData]);

   // Find current plan index
   const currentPlanIndex = useMemo(() => {
      if (!currentPlan || filteredPackageData.length === 0) return -1;

      return filteredPackageData.findIndex((pkg) => pkg.id === currentPlan || pkg.packageId === currentPlan);
   }, [currentPlan, filteredPackageData]);

   useEffect(() => {
      if (filteredPackageData.length > 0) {
         if (isExpired) {
            // When expired, find and select the previous plan
            if (currentPlanIndex !== -1) {
               setSelectedPlan(currentPlanIndex);
               setCurrentSlide(Math.max(0, Math.min(currentPlanIndex, filteredPackageData.length - plansPerSlide)));
            } else {
               // If current plan not found, select first plan
               setSelectedPlan(0);
            }
         } else if (currentPlan) {
            // Set the current plan as selected
            if (currentPlanIndex !== -1) {
               setSelectedPlan(currentPlanIndex);
               // Also set the slide to show the current plan
               setCurrentSlide(Math.max(0, Math.min(currentPlanIndex, filteredPackageData.length - plansPerSlide)));
            }
         } else {
            // If no current plan, select the first one
            setSelectedPlan(0);
         }
      }
   }, [currentPlan, filteredPackageData, isExpired, currentPlanIndex]);

   // Helper function to generate features array from package data
   function generateFeatures(pkg) {
      const features = [];

      if (pkg.noOfUser) {
         features.push(formatUnlimited(pkg.noOfUser, 'Users'));
      }

      if (pkg.noOfCompanies) {
         features.push(formatUnlimited(pkg.noOfCompanies, 'Companies'));
      }

      if (pkg.noOfWp) {
         features.push(formatUnlimited(pkg.noOfWp, 'Daily WhatsApp'));
      }

      if (pkg.noOfEInvoice) {
         features.push(formatUnlimited(pkg.noOfEInvoice, 'E-Invoices'));
      }

      if (pkg.noOfEways) {
         features.push(formatUnlimited(pkg.noOfEways, 'E-Way Bills'));
      }
      return features;
   }

   function formatUnlimited(value, suffix) {
      if (value === -100) return `Unlimited ${suffix}`;
      if (value === 0) return `No ${suffix}`;
      return `${value} ${suffix}`;
   }

   const formatValue = (value) => {
      if (value === -100) return 'Unlimited';
      if (value === 0) return 'None';
      if (value === null || value === undefined) return '-';
      return typeof value === 'number' ? value.toLocaleString() : value;
   };

   function isPopularPlan(packageName) {
      const popularPlans = ['Standard', 'Professional', 'Enterprise'];
      return popularPlans.includes(packageName);
   }

   const hasDiscount = (pkg) => {
      return (pkg.discPer && pkg.discPer > 0) || (pkg.discAmt && pkg.discAmt > 0);
   };

   const getFinalPrice = (pkg) => {
      return pkg.amcPrice;
   };

   const getOriginalPrice = (pkg) => {
      const basePrice = getFinalPrice(pkg);

      if (pkg.discPer && pkg.discPer > 0) {
         return Math.round(basePrice / (1 - pkg.discPer / 100));
      }

      if (pkg.discAmt && pkg.discAmt > 0) {
         return Math.round(basePrice + pkg.discAmt);
      }

      return basePrice;
   };

   const getDiscountAmount = (pkg) => {
      const originalPrice = getOriginalPrice(pkg);
      const finalPrice = getFinalPrice(pkg);
      return originalPrice - finalPrice;
   };

   const getDurationText = (pkg) => {
      const durationType = pkg.durType?.toLowerCase();
      const amcPeriod = pkg.amcPeriod;

      if (!durationType && !amcPeriod) {
         return '/year';
      }

      if (durationType === 'days' || durationType === 'day' || durationType === 'daily') {
         if (amcPeriod === 1) return '/for 1 day';
         if (amcPeriod === 7) return '/for 1 week';
         if (amcPeriod === 15) return '/for 15 days';
         if (amcPeriod === 30) return '/for 1 month';
         return `/for ${amcPeriod} days`;
      }

      if (durationType === 'months' || durationType === 'month' || durationType === 'monthly') {
         if (amcPeriod === 1) return '/for 1 month';
         if (amcPeriod === 3) return '/for 3 months';
         if (amcPeriod === 6) return '/for 6 months';
         if (amcPeriod === 12) return '/for 1 year';
         return `/for ${amcPeriod} months`;
      }

      if (
         durationType === 'years' ||
         durationType === 'year' ||
         durationType === 'yearly' ||
         durationType === 'annual'
      ) {
         if (amcPeriod === 1) return '/for 1 year';
         return `/for ${amcPeriod} years`;
      }

      return `/for ${amcPeriod || ''} ${durationType || ''}`.trim();
   };

   const plansPerSlide = 3;
   const maxSlide = Math.max(0, filteredPackageData.length - plansPerSlide);

   const nextSlide = () => {
      if (currentSlide < maxSlide && !isTransitioning) {
         setIsTransitioning(true);
         setCurrentSlide((prev) => Math.min(prev + 1, maxSlide));
         setTimeout(() => setIsTransitioning(false), 300);
      }
   };

   const prevSlide = () => {
      if (currentSlide > 0 && !isTransitioning) {
         setIsTransitioning(true);
         setCurrentSlide((prev) => Math.max(prev - 1, 0));
         setTimeout(() => setIsTransitioning(false), 300);
      }
   };

   const isCurrentPlan = (plan) => {
      return plan.id === currentPlan || plan.packageId === currentPlan;
   };

   const isSelectedPlan = (index) => {
      return selectedPlan === index;
   };

   // DIRECT RAZORPAY INTEGRATION FOR RENEW
   const handleConfirmUpgrade = async () => {
      if (selectedPlanData && user?.code) {
         setIsProcessingPayment(true);

         try {
            // Close the summary modal
            setShowSummaryModal(false);

            // Create order first
            const addOrderResult = await dispatch(
               AddOrder({
                  amount: getFinalPrice(selectedPlanData),
               }),
            );

            if (addOrderResult && addOrderResult.code === 1) {
               const orderId = addOrderResult.data?.orderId || addOrderResult.orderId;

               if (!orderId) {
                  throw new Error('Order ID not received from server');
               }

               // Prepare notes for RENEW transaction
               const notes = {
                  txnType: 'RENEW',
                  code: user.code,
                  pId: selectedPlanData.id,
               };

               const options = {
                  key: config.RZRPAYKEY,
                  amount: getFinalPrice(selectedPlanData) * 100,
                  order_id: orderId,
                  name: 'Zeronine Tax',
                  currency: 'INR',
                  description: 'Continue payment to purchase Premium plan',
                  timeout: 600,
                  theme: {
                     color: '#3399cc',
                  },
                  notes: notes,
                  handler: async function (response) {
                     // Payment Success

                     toast({
                        title: 'Payment Successful!',
                        description: 'Processing your renewal...',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                     });

                     try {
                        // Call getProfile to refresh user data
                        await dispatch(userProfile());

                        // Show success and redirect
                        toast({
                           title: 'Renewal Successful!',
                           description: 'Your plan has been renewed successfully.',
                           status: 'success',
                           duration: 3000,
                           isClosable: true,
                        });

                        // Redirect to dashboard after renewal
                        setTimeout(() => {
                           window.location.href = '/admin/default';
                        }, 2000);
                     } catch (error) {
                        console.error('Payment processing error:', error);
                        toast({
                           title: 'Error',
                           description: 'Payment successful but processing failed. Please contact support.',
                           status: 'error',
                           duration: 5000,
                           isClosable: true,
                        });
                     } finally {
                        setIsProcessingPayment(false);
                     }
                  },
                  modal: {
                     ondismiss: function () {

                        toast({
                           title: 'Payment Cancelled',
                           description: 'You closed the payment window',
                           status: 'warning',
                           duration: 4000,
                           isClosable: true,
                        });

                        setIsProcessingPayment(false);
                     },
                  },
               };

               const rzp = new window.Razorpay(options);

               rzp.on('payment.failed', function (response) {
                  console.error('❌ Payment Failed!');
                  console.error('Error Code:', response.error.code);
                  console.error('Error Description:', response.error.description);

                  toast({
                     title: 'Payment Failed',
                     description: response.error.description || 'Something went wrong!',
                     status: 'error',
                     duration: 5000,
                     isClosable: true,
                  });

                  setIsProcessingPayment(false);
               });

               // OPEN RAZORPAY
               rzp.open();
            } else {
               throw new Error('Failed to create order');
            }
         } catch (error) {
            console.error('Razorpay payment error:', error);
            toast({
               title: 'Error',
               description: 'Failed to initialize payment. Please try again.',
               status: 'error',
               duration: 5000,
               isClosable: true,
            });
            setIsProcessingPayment(false);
         }
      }
   };

   useEffect(() => {
      const handleKeyDown = (e) => {
         if (e.key === 'Escape') {
            onClose();
         }
      };

      window.addEventListener('keydown', handleKeyDown, true);
      return () => {
         window.removeEventListener('keydown', handleKeyDown, true);
      };
   }, [onClose]);

   // Dark mode classes
   const bgOverlay = isDark ? 'bg-slate-900/80' : 'bg-slate-900/50';
   const modalBg = isDark ? 'bg-slate-800' : 'bg-white';
   const modalBorder = isDark ? 'border-slate-700' : 'border-slate-200';
   const textPrimary = isDark ? 'text-white' : 'text-slate-900';
   const textSecondary = isDark ? 'text-slate-300' : 'text-slate-600';
   const textMuted = isDark ? 'text-slate-400' : 'text-slate-500';
   const buttonHover = isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100';
   const cardBg = isDark ? 'bg-slate-700' : 'bg-white';
   const cardBorder = isDark ? 'border-slate-600' : 'border-slate-200';
   const cardHoverBorder = isDark ? 'border-slate-500' : 'border-slate-300';
   const activeCardBg = isDark ? 'bg-slate-600' : 'bg-slate-50';
   const activeCardBorder = isDark ? 'border-slate-500' : 'border-slate-300';
   const previousCardBg = isDark ? 'bg-blue-900/30' : 'bg-blue-50';
   const previousCardBorder = isDark ? 'border-blue-500' : 'border-blue-300';
   const tableBg = isDark ? 'bg-slate-700' : 'bg-slate-50';
   const tableBorder = isDark ? 'border-slate-600' : 'border-slate-200';
   const disabledButtonBg = isDark ? 'bg-slate-600' : 'bg-slate-200';
   const disabledButtonText = isDark ? 'text-slate-400' : 'text-slate-500';
   const defaultButtonBg = isDark ? 'bg-slate-600' : 'bg-slate-100';
   const defaultButtonText = isDark ? 'text-slate-300' : 'text-slate-700';
   const defaultButtonHover = isDark ? 'hover:bg-slate-500' : 'hover:bg-slate-200';
   const arrowBg = isDark ? 'bg-slate-700' : 'bg-white';
   const arrowHover = isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-50';
   const arrowText = isDark ? 'text-slate-300' : 'text-slate-700';
   const indicatorBg = isDark ? 'bg-slate-500' : 'bg-slate-300';
   const activeIndicatorBg = isDark ? 'bg-blue-500' : 'bg-blue-600';

   const SummaryModal = () => {
      if (!selectedPlanData) return null;

      // Calculate dynamic values based on your existing functions
      const planAmount = getFinalPrice(selectedPlanData);
      const gstPercentage = WebSetting.gstPer || 18;
      const gstAmount = Math.round(planAmount * (gstPercentage / 100));
      const totalAmount = planAmount + gstAmount;
      const cgstAmount = Math.round(gstAmount / 2);
      const sgstAmount = gstAmount - cgstAmount;

      // Get current date and calculate end date (1 year from now)
      const currentDate = new Date();
      const endDate = new Date(currentDate);
      endDate.setFullYear(endDate.getFullYear() + 1);

      const formatDate = (date) => {
         return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
         });
      };

      return (
         <div
            className={`fixed inset-0 ${bgOverlay} backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fadeIn`}>
            <div
               className={`${modalBg} rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-scaleIn flex flex-col border-2 ${isDark ? 'border-blue-500/30' : 'border-blue-200'
                  }`}>
               {/* Header */}
               <div className={`p-6 border-b ${modalBorder} ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                  <div className="flex items-center justify-between">
                     <h2 className={`text-2xl font-bold ${textPrimary}`}>Order Summary</h2>
                     <button
                        onClick={() => setShowSummaryModal(false)}
                        className={`w-10 h-10 rounded-lg ${buttonHover} flex items-center justify-center transition-colors`}>
                        <X className={`w-6 h-6 ${textMuted}`} />
                     </button>
                  </div>
               </div>

               {/* Content */}
               <div className="flex-1 overflow-y-auto p-6">
                  {/* Bill To Section - Using your WebSetting data */}
                  <div className="mb-8">
                     <h3 className={`text-lg font-bold ${textPrimary} mb-4`}>Bill To</h3>
                     <div className="space-y-2">
                        <div className={textPrimary}>{WebSetting.compName || '-'}</div>
                        <div className={textPrimary}>GSTIN : {WebSetting.gstNo || '-'}</div>
                        <div className={`${textPrimary} leading-relaxed`}>Address : {WebSetting.address || '-'}</div>
                     </div>
                  </div>

                  <div className="border-t border-b border-gray-300 my-6"></div>

                  {/* Summary Section - Using your selectedPlanData */}
                  <div className="mb-8">
                     <h3 className={`text-lg font-bold ${textPrimary} mb-4`}>Summary</h3>

                     {/* Plan Description */}
                     <div className="mb-4">
                        <h4 className={`font-bold ${textPrimary} mb-1`}>{selectedPlanData.name || 'Selected Plan'}</h4>
                        <p className={`${textSecondary} text-sm`}>
                           {formatDate(currentDate)} - {formatDate(endDate)} ({selectedPlanData.amcPeriod || '1'}{' '}
                           {selectedPlanData.durType || 'year'})
                        </p>
                     </div>

                     {/* Pricing Table - Using dynamic calculations */}
                     <div className="mb-6">
                        <table className="w-full">
                           <tbody>
                              <tr>
                                 <td className={`py-2 ${textPrimary}`}>Amount</td>
                                 <td className={`py-2 text-right font-medium ${textPrimary}`}>
                                    ₹{selectedPlanData?.subPrice?.toLocaleString()}
                                 </td>
                              </tr>

                              <tr>
                                 <td className={`py-2 ${textPrimary}`}>+ CGST</td>
                                 <td className={`py-2 text-right font-medium ${textPrimary}`}>
                                    ₹{selectedPlanData?.cgst?.toLocaleString()}
                                 </td>
                              </tr>
                              <tr>
                                 <td className={`py-2 ${textPrimary}`}>+ SGST</td>
                                 <td className={`py-2 text-right font-medium ${textPrimary}`}>
                                    ₹{selectedPlanData?.sgst?.toLocaleString()}
                                 </td>
                              </tr>

                              <tr>
                                 <td className={`py-2 ${textPrimary}`}>+ IGST</td>
                                 <td className={`py-2 text-right font-medium ${textPrimary}`}>
                                    ₹{selectedPlanData?.igst?.toLocaleString()}
                                 </td>
                              </tr>

                              {/* Divider above total */}
                              <tr>
                                 <td colSpan={2} className="pt-2">
                                    <div className="border-t border-gray-300 dark:border-gray-600" />
                                 </td>
                              </tr>

                              {/* Total */}
                              <tr>
                                 <td className={`py-3 font-semibold ${textPrimary}`}>
                                    Total Amount
                                 </td>
                                 <td
                                    className={`py-3 text-right font-bold text-lg ${textPrimary}`}
                                 >
                                    ₹{planAmount?.toLocaleString()}
                                 </td>
                              </tr>
                           </tbody>
                        </table>
                     </div>
                  </div>

                  {/* Reviewer Section - Using dynamic data */}
                  {/* <div>
                     <h3 className={`text-lg font-bold ${textPrimary} mb-4`}>Reviewer</h3>
                     <div className="space-y-2">
                        <div className="flex justify-between">
                           <span className={`font-medium ${textSecondary}`}>Total</span>
                           <div className="text-right">
                              <div className={textPrimary}> ₹{planAmount.toLocaleString()}</div>
                              <div className={textPrimary}>CGST: ₹{cgstAmount.toLocaleString()}</div>
                              <div className={textPrimary}>SGST: ₹{sgstAmount.toLocaleString()}</div>
                           </div>
                        </div>
                     </div>
                  </div> */}
               </div>

               {/* Footer */}
               <div className={`border-t ${modalBorder} p-6 ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-6">
                     <div>
                        <p className={`font-bold ${textPrimary}`}>Total Amount</p>
                        <p className={`text-2xl font-bold text-blue-600`}>₹{planAmount.toLocaleString()}</p>
                        <p className={`text-sm ${textMuted}`}>
                           {selectedPlanData.amcPeriod || '1'} {selectedPlanData.durType || 'year'} • Includes GST
                        </p>
                     </div>
                     <div className="text-right">
                        <p className={`text-sm ${textMuted}`}>Including all taxes</p>
                        <p className={`text-sm ${textMuted}`}>No hidden charges</p>
                     </div>
                  </div>

                  <div className="flex gap-3">
                     <button
                        onClick={() => setShowSummaryModal(false)}
                        className={`flex-1 py-3 rounded-xl font-medium transition-all ${isDark
                           ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                           : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-300'
                           }`}>
                        Back to Plans
                     </button>
                     <button
                        onClick={handleConfirmUpgrade}
                        disabled={isProcessingPayment}
                        className="flex-1 py-3 rounded-xl font-bold transition-all bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isProcessingPayment ? (
                           <>
                              <svg
                                 className="animate-spin h-5 w-5"
                                 xmlns="http://www.w3.org/2000/svg"
                                 fill="none"
                                 viewBox="0 0 24 24">
                                 <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"></circle>
                                 <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                           </>
                        ) : (
                           <>
                              <span>Proceed to Payment</span>
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                                 />
                              </svg>
                           </>
                        )}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      );
   };

   return (
      <div
         className={`fixed inset-0 ${bgOverlay} backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn overflow-hidden`}>
         <div
            className={`${modalBg} rounded-2xl max-w-[1500px] w-full max-h-[95vh] overflow-hidden shadow-2xl animate-scaleIn flex flex-col ${modalBorder} border`}>
            {/* Header - Fixed */}
            <div className={`${modalBg} border-b ${modalBorder} p-6 flex items-center justify-between flex-shrink-0`}>
               <div>
                  <h2 className={`text-2xl font-bold ${textPrimary}`}>
                     {isExpired ? 'Renew Your Subscription' : 'Choose Your Perfect Plan'}
                  </h2>
                  <p className={`${textSecondary} mt-1`}>
                     {isExpired ? 'Your subscription has expired. Choose a plan to continue using our services.' : ''}
                  </p>
               </div>
               <button
                  onClick={onClose}
                  className={`w-10 h-10 rounded-lg ${buttonHover} flex items-center justify-center transition-colors`}>
                  <X className={`w-6 h-6 ${textMuted}`} />
               </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
               <div className="p-6">
                  {filteredPackageData.length > 0 ? (
                     <div>
                        {/* Expired Plan Notice */}
                        {isExpired && (
                           <div className="mb-6 bg-gradient-to-r from-red-500 to-rose-600 rounded-xl p-6 text-white text-center">
                              <div className="flex items-center justify-center gap-3 mb-2">
                                 <svg
                                    className="w-6 h-6 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                 </svg>
                                 <h3 className="text-xl font-bold">Your Plan Has Expired</h3>
                              </div>
                              <p className="text-white/90">
                                 Select a new plan to restore access to all premium features
                              </p>
                           </div>
                        )}

                        {/* Plan Cards Section with Relative Positioning */}
                        <div className="relative px-16 mb-12">
                           {/* Navigation Arrows - Now only within plan cards section */}
                           {filteredPackageData.length > plansPerSlide && (
                              <>
                                 <button
                                    onClick={prevSlide}
                                    disabled={currentSlide === 0}
                                    className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full ${arrowBg} shadow-lg flex items-center justify-center transition-all ${currentSlide === 0
                                       ? 'opacity-50 cursor-not-allowed'
                                       : arrowHover + ' hover:shadow-xl'
                                       }`}>
                                    <ChevronLeft className={`w-6 h-6 ${arrowText}`} />
                                 </button>

                                 <button
                                    onClick={nextSlide}
                                    disabled={currentSlide >= maxSlide}
                                    className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full ${arrowBg} shadow-lg flex items-center justify-center transition-all ${currentSlide >= maxSlide
                                       ? 'opacity-50 cursor-not-allowed'
                                       : arrowHover + ' hover:shadow-xl'
                                       }`}>
                                    <ChevronRight className={`w-6 h-6 ${arrowText}`} />
                                 </button>
                              </>
                           )}

                           {/* Carousel Container */}
                           <div className="overflow-hidden">
                              <div
                                 className="flex transition-transform duration-300 ease-out gap-6"
                                 style={{
                                    transform: `translateX(-${currentSlide * (100 / plansPerSlide)}%)`,
                                 }}>
                                 {filteredPackageData.map((plan, idx) => {
                                    const isCurrent = !isExpired && isCurrentPlan(plan);
                                    const isSelected = isSelectedPlan(idx);
                                    const isHovered = hoveredPlan === idx;
                                    const isPreviousPlan = isExpired && isCurrentPlan(plan);

                                    return (
                                       <div
                                          key={plan.id}
                                          onClick={() => setSelectedPlan(idx)}
                                          onMouseEnter={() => setHoveredPlan(idx)}
                                          onMouseLeave={() => setHoveredPlan(null)}
                                          className={`
                                             flex-shrink-0 w-[30%] relative rounded-3xl mb-5 mt-8 ms-4 p-8 transition-all duration-300 cursor-pointer flex flex-col
                                             ${isCurrent
                                                ? `${activeCardBg} border-2 ${activeCardBorder} opacity-60`
                                                : isPreviousPlan
                                                   ? `${previousCardBg} border-2 ${previousCardBorder}`
                                                   : isSelected || isHovered
                                                      ? `${cardBg} shadow-2xl scale-105 border-4 border-blue-500`
                                                      : `${cardBg} border-2 ${cardBorder} hover:${cardHoverBorder} hover:shadow-xl`
                                             }
                                          `}>
                                          {/* Selected Plan Badge - Only show when actually selected, not on hover */}
                                          {isSelected && !isCurrent && (
                                             <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                                                   <Star className="w-4 h-4 fill-current" />
                                                   Selected Plan
                                                </div>
                                             </div>
                                          )}
                                          {/* Current Plan Badge */}
                                          {isCurrent && !isExpired && (
                                             <div className="absolute -top-3 right-4">
                                                <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                                   <Check className="w-3 h-3" />
                                                   CURRENT
                                                </span>
                                             </div>
                                          )}
                                          {/* Previous Plan Badge (when expired) */}
                                          {isPreviousPlan && isExpired && (
                                             <div className="absolute -top-3 right-4">
                                                <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                                                   <Check className="w-3 h-3" />
                                                   PREVIOUS
                                                </span>
                                             </div>
                                          )}
                                          {/* Popular Badge */}
                                          {plan.popular && !isCurrent && !isPreviousPlan && (
                                             <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                                                   MOST POPULAR
                                                </span>
                                             </div>
                                          )}
                                          <div className="mb-6">
                                             <h3
                                                className={`text-2xl font-bold mb-2 ${isCurrent ? textMuted : textPrimary
                                                   }`}>
                                                {plan.name}
                                             </h3>
                                             <p className={`text-sm mb-4 ${isCurrent ? textMuted : textSecondary}`}>
                                                {plan.detail}
                                             </p>

                                             {/* Pricing Section */}
                                             <div className="mb-4">
                                                {hasDiscount(plan) && (
                                                   <div className="flex items-center justify-center gap-2 mb-2">
                                                      <span
                                                         className={`text-xl font-bold line-through ${isCurrent ? textMuted : textSecondary
                                                            }`}>
                                                         ₹{getOriginalPrice(plan).toLocaleString()}
                                                      </span>
                                                      <span
                                                         className={`text-sm font-bold px-2 py-1 rounded ${isCurrent
                                                            ? 'bg-green-100 text-green-600'
                                                            : 'bg-green-500/20 text-green-700'
                                                            }`}>
                                                         Save ₹{getDiscountAmount(plan).toLocaleString()}
                                                      </span>
                                                   </div>
                                                )}

                                                <div className="flex items-baseline gap-2 justify-center">
                                                   <span
                                                      className={`text-4xl font-black ${isCurrent ? 'text-slate-400' : textPrimary
                                                         }`}>
                                                      ₹{getFinalPrice(plan).toLocaleString()}
                                                   </span>
                                                   <span
                                                      className={`text-lg font-bold ${isCurrent ? textMuted : textSecondary
                                                         }`}>
                                                      {getDurationText(plan)}
                                                   </span>
                                                </div>
                                             </div>

                                             {/* Extra Days Info */}
                                             {plan.extraDays > 0 && (
                                                <div
                                                   className={`flex items-center gap-2 ${isDark
                                                      ? 'bg-blue-900/20 border-blue-700'
                                                      : 'bg-blue-50 border-blue-200'
                                                      } border rounded-lg mt-3 px-3 py-2`}>
                                                   <div className="flex items-center gap-2">
                                                      <div className="flex items-baseline gap-1">
                                                         <span
                                                            className={`${isDark ? 'text-blue-300' : 'text-blue-800'
                                                               } font-medium text-sm`}>
                                                            Extra Days: {plan.extraDays}
                                                         </span>
                                                      </div>
                                                      <div className="relative group">
                                                         <div
                                                            className={`w-5 h-5 ${isDark ? 'bg-blue-900/50' : 'bg-blue-100'
                                                               } rounded-full flex items-center justify-center cursor-help ${isDark ? 'hover:bg-blue-800/50' : 'hover:bg-blue-200'
                                                               } transition-colors`}>
                                                            <Info
                                                               className={`w-3 h-3 ${isDark ? 'text-blue-400' : 'text-blue-600'
                                                                  }`}
                                                            />
                                                         </div>

                                                         <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 min-w-[280px] max-w-[320px]">
                                                            <div className="text-center">
                                                               <div className="text-gray-200 leading-relaxed">
                                                                  You've received{' '}
                                                                  <span className="text-green-300 font-semibold">
                                                                     {plan.extraDays} extra days
                                                                  </span>{' '}
                                                                  carried over from the unused days of your previous
                                                                  subscription package.
                                                               </div>
                                                            </div>
                                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-8 border-transparent border-t-gray-900"></div>
                                                         </div>
                                                      </div>
                                                   </div>
                                                </div>
                                             )}
                                          </div>
                                          {/* Features List */}
                                          <div className="flex-1 mb-6">
                                             <ul className="space-y-3">
                                                {plan.features.slice(0, 8).map((feature, featureIdx) => (
                                                   <li key={featureIdx} className="flex items-start gap-2 text-sm">
                                                      <Check
                                                         className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isCurrent ? 'text-green-500' : 'text-green-600'
                                                            }`}
                                                      />
                                                      <span className={isCurrent ? textMuted : textSecondary}>
                                                         {feature}
                                                      </span>
                                                   </li>
                                                ))}
                                                {plan.features.length > 8 && (
                                                   <li
                                                      className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'
                                                         } font-medium`}>
                                                      +{plan.features.length - 8} more features
                                                   </li>
                                                )}
                                             </ul>
                                          </div>
                                          {/* Action Button */}
                                          <button
                                             onClick={() => {
                                                if (!(isCurrent && !isExpired)) {
                                                   setSelectedPlanData(plan);
                                                   setShowSummaryModal(true);
                                                }
                                             }}
                                             disabled={isCurrent && !isExpired}
                                             className={`
      w-full py-4 rounded-2xl font-bold text-lg transition-all transform mt-auto
      ${isCurrent && !isExpired
                                                   ? `${disabledButtonBg} ${disabledButtonText} cursor-not-allowed`
                                                   : isSelected || isHovered
                                                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl hover:shadow-2xl hover:scale-105'
                                                      : isExpired && isPreviousPlan
                                                         ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg hover:shadow-xl'
                                                         : `${defaultButtonBg} ${defaultButtonText} ${defaultButtonHover}`
                                                }
   `}>
                                             {isCurrent && !isExpired
                                                ? 'Current Plan'
                                                : isExpired && isPreviousPlan
                                                   ? 'Renew Previous Plan'
                                                   : 'Get Started'}
                                          </button>
                                       </div>
                                    );
                                 })}
                              </div>
                           </div>

                           {/* Slide Indicators */}
                           {filteredPackageData.length > plansPerSlide && (
                              <div className="flex justify-center gap-2 mt-6">
                                 {Array.from({ length: maxSlide + 1 }).map((_, idx) => (
                                    <button
                                       key={idx}
                                       onClick={() => {
                                          if (!isTransitioning) {
                                             setIsTransitioning(true);
                                             setCurrentSlide(idx);
                                             setTimeout(() => setIsTransitioning(false), 300);
                                          }
                                       }}
                                       className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? `${activeIndicatorBg} w-8` : indicatorBg
                                          }`}
                                    />
                                 ))}
                              </div>
                           )}
                        </div>

                        {/* Complete Comparison Table for Multiple Plans */}
                        {filteredPackageData.length > 1 && (
                           <div className={`mt-12 ${tableBg} rounded-3xl border ${tableBorder} p-8 overflow-x-auto`}>
                              <h3 className={`text-2xl font-bold ${textPrimary} mb-6 text-center`}>
                                 Detailed Feature Comparison
                              </h3>
                              <table className="w-full min-w-[1000px]">
                                 <thead>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <th className={`text-left py-4 ${textPrimary} font-bold pl-4`}>Feature</th>
                                       {filteredPackageData.map((pkg) => (
                                          <th key={pkg.id} className={`text-center py-4 ${textPrimary} font-bold`}>
                                             {pkg.name}
                                          </th>
                                       ))}
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {/* Basic Features */}
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Users</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             <span
                                                className={
                                                   pkg.noOfUser === -100
                                                      ? 'text-green-500 font-semibold'
                                                      : textSecondary
                                                }>
                                                {formatValue(pkg.noOfUser)}
                                             </span>
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Companies</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             <span
                                                className={
                                                   pkg.noOfCompanies === -100
                                                      ? 'text-green-500 font-semibold'
                                                      : textSecondary
                                                }>
                                                {formatValue(pkg.noOfCompanies)}
                                             </span>
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Daily WhatsApp</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             <span
                                                className={
                                                   pkg.noOfWp === -100 ? 'text-green-500 font-semibold' : textSecondary
                                                }>
                                                {formatValue(pkg.noOfWp)}
                                             </span>
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>E-Invoices</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             <span
                                                className={
                                                   pkg.noOfEInvoice === -100
                                                      ? 'text-green-500 font-semibold'
                                                      : textSecondary
                                                }>
                                                {formatValue(pkg.noOfEInvoice)}
                                             </span>
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>E-Way Bills</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             <span
                                                className={
                                                   pkg.noOfEways === -100
                                                      ? 'text-green-500 font-semibold'
                                                      : textSecondary
                                                }>
                                                {formatValue(pkg.noOfEways)}
                                             </span>
                                          </td>
                                       ))}
                                    </tr>

                                    {/* Feature Flags */}
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>CRM System</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.isCrm ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>To-Do Management</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.isToDo ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Mobile App Access</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.isMobileApp ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>GST Dashboard</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.isDashboardGst ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>2-Step Verification</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.is2stepVerify ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>2A/2B Matching</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.is2a2b ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>

                                    {/* Additional Features */}
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Sales Order</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.sales_order ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Purchase Order</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.purchase_order ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Sales Invoice</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.sales_invoice ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Purchase Challan</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.purchase_challan ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Purchase Invoice</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.purchase_invoice ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Sales Return</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.sales_return ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Purchase Return</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.purchase_return ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Issue Management</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.issue ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Receive Management</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.receive ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Cash Management</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.cash ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Bank Management</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.bank ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Journal Voucher</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.jv ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Stock Transfer</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.stock_transfer ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Share Company</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.share_company ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Company-wise Master</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.company_wise_master ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>
                                          Delete Unused Master
                                       </td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.delete_unused_master ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>
                                          Aamne Saamne Sale/Purchase
                                       </td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.aamne_saamne_sale_purchase ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Multiple E-way Bill</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.multiple_eway_bill_generate ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Print Barcode</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.print_barcode ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>Bank Import</td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.bank_import ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                    <tr className={`border-b ${tableBorder}`}>
                                       <td className={`py-4 ${textSecondary} font-medium pl-4`}>
                                          Bulk Update Suspense Account
                                       </td>
                                       {filteredPackageData.map((pkg) => (
                                          <td key={pkg.id} className="text-center py-4">
                                             {pkg.bulk_update_suspense_account ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                             ) : (
                                                <X className="w-5 h-5 text-red-400 mx-auto" />
                                             )}
                                          </td>
                                       ))}
                                    </tr>
                                 </tbody>
                              </table>
                           </div>
                        )}
                     </div>
                  ) : (
                     <div className="text-center py-12">
                        <p className={`${textSecondary} text-lg`}>No plans available</p>
                     </div>
                  )}

                  <div className="mt-8 text-center">
                     <p className={textSecondary}>
                        Need a custom enterprise solution?{' '}
                        <button
                           className={`${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                              } font-medium`}>
                           Contact sales
                        </button>
                     </p>
                  </div>
               </div>
            </div>
         </div>

         {showSummaryModal && <SummaryModal />}

         {/* {showRegistrationForm && (
            <RegistrationForm
               txnType="RENEW"
               userCode={user?.code}
               selectedPlanId={selectedPlanData?.id}
               showUI={false}
               showOtpUI={false}
               showHeader={false}
               showPackageInfo={false}
               onlyDataSubmission={false}
            />
         )} */}

         <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
          .animate-scaleIn { animation: scaleIn 0.3s ease-out; }
        `}</style>
      </div>
   );
};

export default PlanCarousel;
